import { Deserializable } from '../../protocols/deserializable';
import { Variant } from './variant';
import { DisplayAttribute } from '../../display/dto/display-attribute';
import { Cachable } from '../../protocols/cachable';
import { DateUtils } from '../../../utils/date-utils';
import { InventoryProvider } from '../../enum/dto/inventory.provider';
import { StrainType } from '../../enum/dto/strain-classification.enum';
import { UniquelyIdentifiable } from '../../protocols/uniquely-identifiable';
import { StringUtils } from '../../../utils/string-utils';
import { exists } from '../../../functions/exists';

export class Product implements Deserializable, Cachable, UniquelyIdentifiable {
  static buildArrayCacheKey(companyId, locationId: number): string {
    return `Products-${companyId}-${locationId}`;
  }

  static buildCacheKey(companyId, locationId: number, productId: string): string {
    return `Product-${companyId}-${locationId}-${productId}`;
  }

  static empty(): Product {
    const p = new Product();
    p.id = '';
    p.name = '';
    p.isArchived = false;
    p.variants = [];
    p.CBD = '';
    p.THC = '';
    p.classification = StrainType.UNKNOWN;
    p.cachedTime = 0;
    return p;
  }

  // DTO Properties
  public id: string;
  public provider: InventoryProvider;
  public name: string;
  public isArchived: boolean;
  public variants: Variant[];
  public displayAttributes: DisplayAttribute;
  public description: string; // this is only set on custom-made product groupings
  // inherited properties from child variants
  public CBD: string;
  public THC: string;
  public classification: StrainType;
  // Cache
  public cachedTime: number;

  public onDeserialize() {
    this.deserializeVariants();
    this.displayAttributes = window?.injector?.Deserialize?.instanceOf(DisplayAttribute, this.displayAttributes);
    // Set variant properties for parent product
    this.variants.forEach(v => {
      v.productId = this.id;
      v.productName = this.name;
      return v;
    });
  }

  protected deserializeVariants() {
    this.variants = window?.injector?.Deserialize?.arrayOf(Variant, this.variants) || [];
  }

  mergeWithTemplateProduct(templateProduct: Product) {
    if (!templateProduct) return;
    if (!this.variants) this.variants = [];
    templateProduct?.variants?.forEach(templateVariant => {
      const existingVariant = this.variants.find(v => v?.id === templateVariant?.id);
      if (!existingVariant) this.variants.push(templateVariant);
    });
  }

  cacheExpirySeconds(): number {
    return DateUtils.unixOneDay();
  }

  cacheKey(companyId, locationId: number): string {
    return Product.buildCacheKey(companyId, locationId, this.id);
  }

  isExpired(): boolean {
    const expiresAt = this.cachedTime + this.cacheExpirySeconds();
    return DateUtils.nowInUnixSeconds() > expiresAt;
  }

  public setImplicitProperties(
    enabledVariantIds: string[] | null,
    secondaryPriceGroupId: string | null,
    locationTimezone: string | null = null
  ) {
    // Promotions carry wall-clock windows evaluated in the store's timezone. The API sends that timezone on
    // the promotion itself; this only backfills it for payloads that predate the API change, or locations
    // where it is unset. An API-provided value always wins.
    this.variants?.forEach(v => {
      const promotion = v?.locationPromotion;
      if (exists(promotion) && !promotion.timezone && exists(locationTimezone)) promotion.timezone = locationTimezone;
    });
    // Set THC/CBD/Classification on product
    const minTHC = Math.min(...this.variants.map(v => v.getNumericCannabinoidOrTerpene('THC'))) ?? 0;
    const minCBD = Math.min(...this.variants.map(v => v.getNumericCannabinoidOrTerpene('CBD'))) ?? 0;
    this.THC = `${minTHC}`;
    this.CBD = `${minCBD}`;
    // Set the label property on the variants
    if (enabledVariantIds === null) {
      this.classification = StringUtils.getStringModeOrNull(this.variants?.map(v => v?.classification));
    } else {
      this.classification = StringUtils.getStringModeOrNull(
        this.variants?.filter(v => enabledVariantIds.contains(v.id))?.map(v => v?.classification)
      );
    }
    // Compute secondary company price if secondaryPriceGroupId is set on location configuration
    if (exists(secondaryPriceGroupId)) {
      this.computeAndSetSecondaryCompanyPrice(secondaryPriceGroupId);
    }
  }

  computeAndSetSecondaryCompanyPrice(secondaryPriceGroupId: string): void {
    this.variants?.forEach(v => {
      const companyPricing = v?.locationPricing?.find(lp => lp.locationId === v.companyId);
      if (exists(companyPricing)) {
        companyPricing.secondaryPrice = companyPricing?.pricingGroupPrices?.[secondaryPriceGroupId] || null;
      }
    });
  }

  getProductTitle(): string {
    const hasDisplayAttributeName = exists(this.displayAttributes) && exists(this.displayAttributes?.displayName);
    const hasInheritedDisplayAttributeName =
      exists(this.displayAttributes?.inheritedDisplayAttribute) &&
      exists(this.displayAttributes?.inheritedDisplayAttribute?.displayName);
    if (hasDisplayAttributeName) {
      return this.displayAttributes.displayName;
    }
    if (hasInheritedDisplayAttributeName) {
      return this.displayAttributes.inheritedDisplayAttribute.displayName;
    }
    return this.name;
  }

  public getBrand(variantIds: string[] = null): string | null {
    const selectedVariants = this.getSelectedVariants(variantIds);
    return StringUtils.getStringModeOrNull(selectedVariants?.map(v => v?.brand));
  }

  public getStrain(variantIds: string[] = null): string | null {
    const selectedVariants = this.getSelectedVariants(variantIds);
    return StringUtils.getStringModeOrNull(selectedVariants?.map(v => v?.strain));
  }

  public getLineage(variantIds: string[] = null): string | null {
    const selectedVariants = this.getSelectedVariants(variantIds);
    return StringUtils.getStringModeOrNull(selectedVariants?.map(v => v?.getLineagesAsString()));
  }

  public getManufacturer(variantIds: string[] = null): string | null {
    const selectedVariants = this.getSelectedVariants(variantIds);
    return StringUtils.getStringModeOrNull(selectedVariants?.map(v => v?.manufacturer));
  }

  public getEffects(variantIds: string[] = null): string | null {
    const selectedVariants = this.getSelectedVariants(variantIds);
    return StringUtils.getStringModeOrNull(selectedVariants?.map(v => v?.getEffectsAsString()));
  }

  public getAromas(variantIds: string[] = null): string | null {
    const selectedVariants = this.getSelectedVariants(variantIds);
    return StringUtils.getStringModeOrNull(selectedVariants?.map(v => v?.getAromasAsString()));
  }

  public getFlavorGroup(variantIds: string[] = null): string | null {
    const selectedVariants = this.getSelectedVariants(variantIds);
    return StringUtils.getStringModeOrNull(selectedVariants?.map(v => v?.flavorGroup));
  }

  public getFlavors(variantIds: string[] = null): string | null {
    const selectedVariants = this.getSelectedVariants(variantIds);
    return StringUtils.getStringModeOrNull(selectedVariants?.map(v => v?.getFlavorsAsString()));
  }

  public getHelpsWith(variantIds: string[] = null): string | null {
    const selectedVariants = this.getSelectedVariants(variantIds);
    return StringUtils.getStringModeOrNull(selectedVariants?.map(v => v?.getHelpsWithAsString()));
  }

  public getAppearances(variantIds: string[] = null): string | null {
    const selectedVariants = this.getSelectedVariants(variantIds);
    return StringUtils.getStringModeOrNull(selectedVariants?.map(v => v?.getAppearancesAsString()));
  }

  public getGrowingConditions(variantIds: string[] = null): string | null {
    const selectedVariants = this.getSelectedVariants(variantIds);
    return StringUtils.getStringModeOrNull(selectedVariants?.map(v => v?.getGrowingConditionsAsString()));
  }

  public getCraftDesignations(variantIds: string[] = null): string | null {
    const selectedVariants = this.getSelectedVariants(variantIds);
    return StringUtils.getStringModeOrNull(selectedVariants?.map(v => v?.getCraftDesignationsAsString()));
  }

  private getSelectedVariants(variantIds: string[]): Variant[] {
    let selectedVariants: Variant[];
    if (variantIds?.length > 0) {
      selectedVariants = this.variants.filter(v => variantIds.contains(v.id));
    } else {
      selectedVariants = this.variants;
    }
    return selectedVariants;
  }

  getStrainType(): StrainType {
    return StringUtils.getStringModeOrNull(this.variants?.map(v => v?.classification));
  }

  getUniqueIdentifier(): string {
    const variantsId = this.variants
      .map(v => v?.getUniqueIdentifier())
      .sort()
      .join(',');
    return `
      -${this.id}
      -${this.name}
      -${variantsId}
      -${this.displayAttributes?.getUniqueIdentifier()}
    `;
  }
}
